import { QueryClient } from '@tanstack/react-query';
import Constants from 'expo-constants';

const environment = Constants.expoConfig?.extra?.appVariant || 'development';
const isProduction = environment === 'production';
const isStaging = environment === 'staging';

const DEFAULT_STALE_TIME = (isProduction || isStaging)
  ? 30 * 1000
  : 15 * 1000;

export const CATALOG_STALE_TIME = (isProduction || isStaging)
  ? 30 * 60 * 1000 // 30 minutes in production or staging
  : 1 * 60 * 1000;

export const USER_DATA_STALE_TIME = 0; // 0 seconds
export const LIVE_SESSIONS_STALE_TIME = 2 * 60 * 1000;

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: DEFAULT_STALE_TIME,
      gcTime: 5 * 60 * 1000, // 5 minutes garbage collection
      retry: 1,
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
    },
  },
});

export const queryKeys = {
  course: {
    bySlug: (slug: string) => ['course', slug] as const,
    sections: (courseId: number, minStatus: number) => 
      ['course', courseId, 'sections', minStatus] as const,
    collections: (courseId: number) => 
      ['course', courseId, 'collections'] as const,
    courseThumbnailsCategories: (courseId: number) =>
      ['course', courseId, 'courseThumbnailsCategories'] as const,
    latestLessons: (courseId: number, limit: number, minStatus: number) => 
      ['course', courseId, 'latest', limit, minStatus] as const,
    lessons: (courseId: number, minStatus: number) =>
      ['course', courseId, 'lessons', minStatus] as const,
    skillPathways: (courseId: number) =>
      ['course', courseId, 'skillPathways'] as const,
  },
  userLessons: {
    byCourse: (userId: string | null, courseId: number) => 
      ['userLessons', userId, courseId] as const,
    continueForDashboard: (userId: string | null, courseId: number, simpleCourseIds: number[]) =>
      ['userLessons', 'continueForDashboard', userId, courseId, simpleCourseIds] as const,
    courseProgress: (userId: string | null, courseIds: number[]) =>
      ['userCourseProgress', userId, courseIds] as const,
    all: (userId: string | null) => 
      ['userLessons', userId] as const,
  },
  liveSessions: ['live-sessions'] as const,
  announcements: (userId: string | undefined) =>
    ['announcements', userId ?? 'none'] as const,
  subscriptionState: (userId: string | undefined) =>
    ['subscriptionState', userId ?? 'none'] as const,
  hubCtas: (targets: string[]) =>
    ["hubCtas", ...targets] as const,
  purchasedCourses: {
    category: (userId: string | null) =>
      ['purchasedCourses', 'category', userId] as const,
    continue: (userId: string | null) =>
      ['purchasedCourses', 'continue', userId] as const,
  },
  courses: {
    catalogHub: (userId: string | null, minStatus: number) =>
      ['courses', 'catalogHub', userId, minStatus] as const,
  },
  instructors: {
    all: ['instructors', 'all'] as const,
    bySlug: (slug: string) => ['instructors', 'bySlug', slug] as const,
    courseIds: (instructorId: number | null) =>
      ['instructors', 'courseIds', instructorId] as const,
  },
} as const;

